import React, {  useState } from "react";
import Select from "react-select";
import { compactSelect, compactSelectError } from "../../../design/selectStyle";
import { useTranslation } from "react-i18next";
import { faFileInvoice, faFloppyDisk } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import currencies from "../../../services/currencies.json";
import { toast } from "react-toastify";
import { validateForm } from "../../../services/functions";
import TaxPayerSearch from "../../global/TaxPayerSearch";
import { useGetFinantialInstitutions } from "../../OpenBanking/OpenBankingService";
import { sellerBankAccountValidateFields } from "./PaymentOrderUtils";
import { useAppContext } from "../../../AppContext";
import mainAxios from "../../../services/mainAxios";
import PopOver from "../../global/PopOver";

const EditSellerBankAccountModal = ({
  paymentOrder,
  onSave,
  errorsAbove = {},
}) => {
  const { t } = useTranslation("translations");
  const { user: [user], selectedCompany: [selectedCompany] } = useAppContext();

  const [localBankAccount, setLocalBankAccount] = useState({...paymentOrder?.sellerBankAccountTransactional ?? {}, creditorAccountReferenceType: "IBAN"});

  const [errors, setErrors] = useState({});
  const [saveInSystemLoading, setSaveInSystemLoading] = useState(false);
  const {data:bankList} = useGetFinantialInstitutions()

  const customStyle = {
    borderRadius: "40%",
    width: "20px",
    height: "20px",
    transform: "scale(0.80)",
    backgroundColor: "#343a40",
    color: "white",
  }


  const handleLocalChange = (key, value) => {
    if(errors[key]){
      setErrors((errors)=>({...errors,[key]:null}))
    }
    setLocalBankAccount((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleFinantialInstitutionChange = (finantialInstitution)=>{
    if(errors.creditorAgent){
      setErrors((errors)=>({...errors,creditorAgent:null}))
    }
    setLocalBankAccount((prev) => ({
      ...prev,
      creditorAgent:finantialInstitution.bic,
      creditorBankName:finantialInstitution.name
    }));
  }

  const handleSave = (e) => {
    e.preventDefault();
    const validationObj = validateForm(sellerBankAccountValidateFields, localBankAccount);

    if (!validationObj.isValid || Object.keys(validationObj.errors).length > 0) {
      toast.error(t("common.fixValidations"),{ containerId: "all_toast" })
      setErrors(validationObj.errors);
      return false;
    }else{
      onSave(localBankAccount);
    }

  };

  const handleSaveInSystem = async (e) => {
    e.preventDefault();
    const validationObj = validateForm(sellerBankAccountValidateFields, localBankAccount);

    if (!validationObj.isValid || Object.keys(validationObj.errors).length > 0) {
      toast.error(t("common.fixValidations"),{ containerId: "all_toast" })
      setErrors(validationObj.errors);
      return false;
    }

    if (!localBankAccount?.contactId) {
      toast.error(t("common.error"), { containerId: "all_toast" });
      return;
    }

    setSaveInSystemLoading(true);
    try {
      const nuis = user.companiesRights.find(
        (company) => company.id === parseInt(selectedCompany)
      )?.nuis;

      const searchResponse = await mainAxios.post("apiEndpoint/search", {
        object: "GetConnections",
        params: {
          id: String(localBankAccount.contactId),
        },
      });

      if (!searchResponse?.data?.status || !searchResponse?.data?.content?.[0]) {
        toast.error(t("common.error"), { containerId: "all_toast" });
        return;
      }

      const contact = searchResponse.data.content[0];

      const selectedBank = bankList.find(
        (bank) => bank.bic === localBankAccount.creditorAgent
      );

      const newAccount = {
        financialInstitution: selectedBank,
        accountNo: localBankAccount.creditorAccountReference,
        iban: localBankAccount.sellerIban,
        currency: localBankAccount.creditorAccountReferenceCurrency,
        defaultValue:true
      };

      const accountExists = contact.accounts?.some(
        (acc) => acc.iban === localBankAccount.sellerIban
      );

      const updatedAccounts = accountExists
        ? contact.accounts?.map((acc) =>
            acc.iban === localBankAccount.sellerIban
              ? {
                  ...acc,
                  ...newAccount,
                  defaultValue:acc.defaultValue
                }
              : acc
          ) || []
        : [...(contact.accounts || []), newAccount];

      const saveResponse = await mainAxios.post("/apiEndpoint/saveOrUpdate", {
        object: "Connections",
        nuis: nuis,
        username: user.username,
        content: {
          ...contact,
          accounts: updatedAccounts,
        },
      });

      if (saveResponse?.data?.status) {
        toast.success(t("mySuppliers.updateContact"), {
          containerId: "all_toast",
        });
        onSave(localBankAccount);
      } else {
        toast.error(saveResponse?.data?.message || t("common.error"), {
          containerId: "all_toast",
        });
      }
    } catch (error) {
      toast.error(t("common.error"), { containerId: "all_toast" });
    } finally {
      setSaveInSystemLoading(false);
    }
  };

  const onTaxPayerChange=(taxPayer)=>{
    let { name, tin } = taxPayer
    setLocalBankAccount((state) => ({
      ...state,
      sellerName:name,
      sellerNipt:tin
    }))
  }

  const hasFieldError = (fieldName) => {
    return errorsAbove?.[fieldName] || errors?.[fieldName];
  };

  const getFieldError = (fieldName) => {
    return errorsAbove?.[fieldName] || errors?.[fieldName];
  };


  return (
    <div className="row  px-2 ">
      <form onSubmit={handleSave} className="col-12 mx-auto">
        <div className="row">
            <div className="col-12 mb-2">
              <TaxPayerSearch
                onSelect={(taxPayer)=>onTaxPayerChange(taxPayer)}
                size={"md"}
              />
            </div>
          <div className="col-12 mb-2">
              <label className="mb-1" htmlFor="sellerName">
                {t("openBanking.vendorName")}
              </label>
              <input
                type="text"
                className={`form-control ${
                  hasFieldError("sellerName") ? "is-invalid" : ""
                }`}
                id="sellerName"
                name="sellerName"
                value={localBankAccount.sellerName || ""}
                onChange={(e) => handleLocalChange("sellerName", e.target.value)}
              />
              {getFieldError("sellerName")?.message && (
                <div className="invalid-feedback">{getFieldError("sellerName")?.message}</div>
              )}
          </div>
          <div className="col-12 mb-2">
              <label className="mb-1" htmlFor="sellerNipt">
                {t("openBanking.sellerNipt")}
              </label>
              <input
                type="text"
                className={`form-control ${
                  hasFieldError("sellerNipt") ? "is-invalid" : ""
                }`}
                id="sellerNipt"
                name="sellerNipt"
                value={localBankAccount.sellerNipt || ""}
                onChange={(e) => handleLocalChange("sellerNipt", e.target.value)}
              />
              {getFieldError("sellerNipt")?.message && (
                <div className="invalid-feedback">{getFieldError("sellerNipt")?.message}</div>
              )}
          </div>
          <div className="col-12 mb-2">
              <label className="mb-1" htmlFor="sellerIban">IBAN</label>
              <input
                type="text"
                className={`form-control ${hasFieldError("sellerIban") ? "is-invalid" : ""}`}
                id="sellerIban"
                name="sellerIban"
                placeholder="E.g 'AL472121100900000002356987731'"
                value={localBankAccount.sellerIban || ""}
                onChange={(e) => handleLocalChange("sellerIban", e.target.value)}
              />
              {getFieldError("sellerIban")?.message && (
                <div className="invalid-feedback">{getFieldError("sellerIban")?.message}</div>
              )}
          </div>
           <div className="col-12 mb-2">
              <label className="mb-1" htmlFor="currency">{t("openBanking.financialInstitution")}</label>
              <Select
                options={bankList}
                styles={{
                  ...(hasFieldError("creditorAgent") ?compactSelectError:compactSelect),
                  menuPortal: (base) => ({ ...base, zIndex: 10001 }),
                }}
                value={bankList.find(
                  (bank) => bank.bic === localBankAccount.creditorAgent
                )}
                placeholder={localBankAccount.creditorBankName}
                getOptionLabel={(opt)=>opt.name}
                getOptionValue={(opt)=>opt.bic}
                isSearchable={true}
                onChange={(opt) =>
                  handleFinantialInstitutionChange(opt)
                }
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {getFieldError("creditorBankName")?.message && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {getFieldError("creditorBankName")?.message}
                </div>
              )}
          </div>
          <div className="col-12 mb-2">
              <label className="mb-1" htmlFor="creditorAccountReference">
                {t("openBanking.bankAccountNumber")}
              </label>
              <input
                type="text"
                className={`form-control ${
                  hasFieldError("creditorAccountReference") ? "is-invalid" : ""
                }`}
                id="creditorAccountReference"
                name="creditorAccountReference"
                value={localBankAccount.creditorAccountReference || ""}
                onChange={(e) => handleLocalChange("creditorAccountReference", e.target.value)}
              />
              {getFieldError("creditorAccountReference")?.message && (
                <div className="invalid-feedback">{getFieldError("creditorAccountReference")?.message}</div>
              )}
          </div>
          <div className="col-12 mb-2">
              <label className="mb-1" htmlFor="currency">{t("openBanking.currency")}</label>
              <Select
                options={currencies}
                styles={{
                  ...(hasFieldError("creditorAccountReferenceCurrency") ?compactSelectError:compactSelect),
                  menuPortal: (base) => ({ ...base, zIndex: 10001 }),
                }}
                value={currencies.find(
                  (currency) => currency.value === localBankAccount.creditorAccountReferenceCurrency
                )}
                isSearchable={true}
                onChange={(opt) =>
                  handleLocalChange("creditorAccountReferenceCurrency", opt.value)
                }
                name="creditorAccountReferenceCurrency"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {getFieldError("creditorAccountReferenceCurrency")?.message && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {getFieldError("creditorAccountReferenceCurrency")?.message}
                </div>
              )}
          </div>
          <div className="col-12 mt-4 d-flex gap-2 m-0 py-0">
            <button
              type="submit"
              style={{ minWidth: "100px" }}
              className="btn btn-outline-primary"
            >
              <FontAwesomeIcon icon={faFileInvoice} className="mr-2" />
              {t("common.save")}
            </button>
            {localBankAccount?.contactId && (
              <div className="d-flex align-items-center">
              <button
                type="button"
                style={{ minWidth: "100px" }}
                className="btn btn-primary ml-2"
                onClick={handleSaveInSystem}
                disabled={saveInSystemLoading}
              >
                {saveInSystemLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    {t("invoice.saveInSystem")}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faFileInvoice} className="mr-2" />
                    {t("invoice.saveInSystem")}
                  </>
                )}
              </button>
              <PopOver
                customStyle={customStyle}
                title={t("paymentOrder.saveInSystemTooltip")}
              />
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditSellerBankAccountModal;
